import { batch, signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { identity, NOTIFICATION_EVENTS } from "deso-protocol";
import type { ReactNode } from "react";
import { use, useCallback } from "react";
import { createContext, useEffect, useLayoutEffect, useState } from "react";

import { toast } from "@/components/ui/use-toast";
import { setClientCookie } from "@/utils/client-utils";
import { CookieKeys } from "@/utils/cookie-keys";

type AlternateUsers = { [publicKey: string]: { publicKey: string } };

export type AuthContextType = {
  currentUserPublicKey: string;
  setCurrentUserPublicKey: (publicKey: string) => void;
  alternateUsers: AlternateUsers;
  loggedIn: boolean;
};

export const AuthContext = createContext<AuthContextType>(null!);
export const currentUserPublicKeySignal = signal("");
export const alternateUsersSignal = signal<AlternateUsers>({});
export const loggedInSignal = signal(false);

const setAuthSignals = ({
  alternateUsers,
  currentUserPublicKey,
}: {
  alternateUsers?: AlternateUsers;
  currentUserPublicKey?: string;
}) => {
  batch(() => {
    if (currentUserPublicKey !== undefined) {
      currentUserPublicKeySignal.value = currentUserPublicKey;
      loggedInSignal.value = !!currentUserPublicKey;
    }

    if (alternateUsers !== undefined) {
      alternateUsersSignal.value = alternateUsers;
    }
  });
};

export const useAuthSignalState = () => {
  useSignals();

  return {
    alternateUsers: alternateUsersSignal.value,
    currentUserPublicKey: currentUserPublicKeySignal.value,
    loggedIn: loggedInSignal.value,
  };
};

if (typeof window !== "undefined") {
  // @ts-expect-error no need for type
  window.identity = identity;
}

export const AuthContextProvider = ({
  children,
  cookiePublicKeyFromServer = "",
}: {
  children: ReactNode;
  cookiePublicKeyFromServer: string;
}) => {
  const [currentUserPublicKeyState, setCurrentUserPublicKeyState] =
    useState<string>(cookiePublicKeyFromServer || "");
  const currentUserPublicKey = currentUserPublicKeyState;

  const setCurrentUserPublicKey = useCallback((publicKey: string) => {
    setAuthSignals({
      currentUserPublicKey: publicKey,
    });
    setCurrentUserPublicKeyState(publicKey);
  }, []);

  const [alternateUsers, setAlternateUsersState] = useState<AlternateUsers>({});
  const setAlternateUsers = useCallback((users: AlternateUsers) => {
    setAuthSignals({
      alternateUsers: users,
    });
    setAlternateUsersState(users);
  }, []);

  /*
    // In a lot of cases in the codebase functions used a string type for cookie public key
    // This way all types will stay compatible, but `if (cookiePublicKeyFromServer)` checks will
    // fail as intended.
  */

  useLayoutEffect(() => {
    setAuthSignals({
      alternateUsers,
      currentUserPublicKey,
    });
  }, [alternateUsers, currentUserPublicKey]);

  useEffect(() => {
    return () => {
      setAuthSignals({
        alternateUsers: {},
        currentUserPublicKey: "",
      });
    };
  }, []);

  useEffect(() => {
    setClientCookie(CookieKeys.publicKey, currentUserPublicKey);
  }, [currentUserPublicKey]);

  useEffect(() => {
    let isActive = true;

    void Promise.resolve(identity.snapshot())
      .then((snapshot) => {
        if (!isActive) {
          return;
        }

        const { alternateUsers, currentUser } = snapshot;

        setCurrentUserPublicKey(currentUser?.publicKey || "");
        setAlternateUsers(alternateUsers || {});
      })
      .catch((error: unknown) => {
        console.error("Failed to hydrate identity snapshot", error);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(
    () => {
      identity.subscribe(async ({ event, currentUser, alternateUsers }) => {
        setCurrentUserPublicKey(currentUser?.publicKey || "");
        setAlternateUsers(alternateUsers || {});

        if (event === NOTIFICATION_EVENTS.AUTHORIZE_DERIVED_KEY_FAIL) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "There was a problem to authorize your derived key.",
          });
        }

        if (event === NOTIFICATION_EVENTS.LOGOUT_END) {
          setCurrentUserPublicKey("");
        }
      });
    },

    [
      /*
        NOTE: it is very important that we DO NOT add dependencies here. We only want this to run ONCE
        https://reactjs.org/docs/hooks-effect.html (see the Note section at the bottom of the page)
      */
    ],
  );

  const contextValue = {
    currentUserPublicKey,
    setCurrentUserPublicKey,
    alternateUsers,
    loggedIn: !!currentUserPublicKey,
  };

  return <AuthContext value={contextValue}>{children}</AuthContext>;
};

export const useAuthContext = () => {
  return use(AuthContext);
};
